import React, { useLayoutEffect, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

const EMOTION_LIGHT = {
  joy: "#f3c96b",
  calm: "#7aa2ff",
  excited: "#ff8a5b",
  curious: "#c084fc",
  neutral: "#a78bfa",
};

const clamp01 = (n) => Math.min(1, Math.max(0, n));
const lerp = (a, b, t) => a + (b - a) * t;

function GoodBoiRig({ emotion = "neutral", animation = "idle", speaking = false, thinking = false, audioLevel = 0 }) {
  const root = useRef();
  const hips = useRef();
  const torso = useRef();
  const head = useRef();
  const jaw = useRef();
  const tail = useRef();
  const earL = useRef();
  const earR = useRef();
  const pupilL = useRef();
  const pupilR = useRef();
  const lidL = useRef();
  const lidR = useRef();
  const armL = useRef();
  const armR = useRef();
  const blink = useRef({ t: 2.4, open: 1 });

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const dt = Math.min(delta, 0.05);
    const em = String(emotion || "neutral").toLowerCase();
    const anim = thinking ? "thinking" : String(animation || "idle").toLowerCase();
    const talk = speaking || anim === "talk";
    const happy = anim === "happy" || em === "joy" || em === "excited";
    const excited = em === "excited";
    const curious = em === "curious" || anim === "thinking";
    const calm = em === "calm";

    const breath = Math.sin(t * (calm ? 1.2 : 1.7)) * (calm ? 0.012 : 0.02);
    const bounce = happy ? Math.abs(Math.sin(t * (excited ? 8 : 6))) * 0.08 : talk ? Math.sin(t * 5) * 0.012 : 0;
    if (root.current) root.current.position.y = bounce;
    if (hips.current) hips.current.rotation.y = Math.sin(t * (talk ? 1.6 : 0.6)) * (talk ? 0.08 : 0.04);
    if (torso.current) {
      torso.current.scale.y = 1 + breath;
      torso.current.rotation.x = lerp(torso.current.rotation.x, anim === "thinking" ? 0.12 : talk ? -0.06 : 0.02, 1 - Math.pow(0.08, dt));
      torso.current.rotation.z = Math.sin(t * 0.8) * 0.03;
    }

    const headTilt = curious ? 0.22 : happy ? -0.08 : 0;
    const headYaw = Math.sin(t * (curious ? 1.4 : 0.5)) * (curious ? 0.18 : 0.06);
    const nod = talk ? Math.sin(t * 7) * 0.07 : 0;
    if (head.current) {
      head.current.rotation.z = lerp(head.current.rotation.z, headTilt, 0.12);
      head.current.rotation.y = lerp(head.current.rotation.y, headYaw, 0.1);
      head.current.rotation.x = nod + (anim === "thinking" ? 0.18 : -0.04);
    }

    const level = clamp01(audioLevel);
    const chatter = talk ? 0.08 + level * 0.55 + Math.abs(Math.sin(t * 16)) * 0.18 : 0;
    if (jaw.current) {
      jaw.current.rotation.x = lerp(jaw.current.rotation.x, chatter, talk ? 0.35 : 0.18);
      jaw.current.position.y = -0.08 - chatter * 0.04;
    }

    const wag = happy ? 2.8 : talk ? 1.6 : calm ? 0.6 : 1.1;
    const wagAmp = happy ? 0.7 : talk ? 0.35 : 0.18;
    if (tail.current) {
      tail.current.rotation.x = 0.45;
      tail.current.rotation.y = Math.sin(t * wag) * wagAmp;
      tail.current.rotation.z = Math.sin(t * wag * 0.5) * 0.1;
    }

    const perk = curious || excited || happy ? 0.35 : -0.15;
    if (earL.current) {
      earL.current.rotation.z = 0.55 + perk;
      earL.current.rotation.x = 0.15 + Math.sin(t * 2.1) * 0.05;
    }
    if (earR.current) {
      earR.current.rotation.z = -0.55 - perk;
      earR.current.rotation.x = 0.15 + Math.sin(t * 2.3 + 1) * 0.05;
    }

    blink.current.t -= dt;
    if (blink.current.t <= 0) {
      blink.current.open = blink.current.open > 0.5 ? 0.08 : 1;
      blink.current.t = blink.current.open < 0.5 ? 0.08 : 2.2 + Math.random() * 2.8;
    }
    if (lidL.current) lidL.current.scale.y = blink.current.open;
    if (lidR.current) lidR.current.scale.y = blink.current.open;

    const lookX = Math.sin(t * 0.35) * 0.03 + (curious ? 0.02 : 0);
    const lookY = Math.sin(t * 0.27) * 0.02;
    if (pupilL.current) pupilL.current.position.set(-0.09 + lookX, 0.06 + lookY, 0.325);
    if (pupilR.current) pupilR.current.position.set(0.09 + lookX, 0.06 + lookY, 0.325);

    if (armL.current) {
      const targetX = happy ? -1.1 : anim === "thinking" ? -0.35 : -0.2;
      armL.current.rotation.x = lerp(armL.current.rotation.x, targetX + Math.sin(t * 2) * 0.05, 0.1);
      armL.current.rotation.z = lerp(armL.current.rotation.z, happy ? 0.55 : 0.22, 0.1);
    }
    if (armR.current) {
      const wave = talk ? -0.55 + Math.sin(t * 6) * 0.35 : happy ? -1.05 : anim === "thinking" ? -1.15 : -0.15;
      armR.current.rotation.x = lerp(armR.current.rotation.x, wave, 0.12);
      armR.current.rotation.z = lerp(armR.current.rotation.z, anim === "thinking" ? -0.15 : happy ? -0.5 : -0.2, 0.1);
    }
  });

  const fur = "#f3d7ad";
  const furDark = "#c4925c";
  const hoodie = "#4a4a5c";
  const accent = "#b794ff";
  const pants = "#3a3a4a";

  return (
    <group ref={root}>
      <group ref={hips} position={[0, 0.92, 0]}>
        <mesh position={[-0.13, -0.42, 0]} castShadow>
          <capsuleGeometry args={[0.09, 0.42, 6, 10]} />
            <meshStandardMaterial color={pants} roughness={0.55} />
        </mesh>
        <mesh position={[0.13, -0.42, 0]} castShadow>
          <capsuleGeometry args={[0.09, 0.42, 6, 10]} />
          <meshStandardMaterial color={pants} roughness={0.55} />
        </mesh>
        <mesh position={[-0.13, -0.72, 0.06]} castShadow>
          <boxGeometry args={[0.16, 0.08, 0.24]} />
          <meshStandardMaterial color="#0d0d12" />
        </mesh>
        <mesh position={[0.13, -0.72, 0.06]} castShadow>
          <boxGeometry args={[0.16, 0.08, 0.24]} />
          <meshStandardMaterial color="#0d0d12" />
        </mesh>

        <group ref={torso} position={[0, 0.02, 0]}>
          <mesh position={[0, 0.28, 0]} castShadow>
            <capsuleGeometry args={[0.28, 0.38, 8, 16]} />
            <meshStandardMaterial color={hoodie} roughness={0.45} />
          </mesh>
          <mesh position={[0, 0.18, 0.22]} castShadow>
            <boxGeometry args={[0.08, 0.34, 0.04]} />
            <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={0.25} />
          </mesh>
          <mesh position={[0, 0.52, 0.02]} castShadow>
            <torusGeometry args={[0.2, 0.05, 8, 20]} />
            <meshStandardMaterial color="#12121a" />
          </mesh>

          <group ref={armL} position={[-0.34, 0.42, 0]} rotation={[0, 0, 0.25]}>
            <mesh position={[0, -0.22, 0]} castShadow>
              <capsuleGeometry args={[0.065, 0.34, 6, 10]} />
              <meshStandardMaterial color={hoodie} />
            </mesh>
            <mesh position={[0, -0.44, 0]} castShadow>
              <sphereGeometry args={[0.07, 12, 12]} />
              <meshStandardMaterial color={fur} />
            </mesh>
          </group>
          <group ref={armR} position={[0.34, 0.42, 0]} rotation={[0, 0, -0.25]}>
            <mesh position={[0, -0.22, 0]} castShadow>
              <capsuleGeometry args={[0.065, 0.34, 6, 10]} />
              <meshStandardMaterial color={hoodie} />
            </mesh>
            <mesh position={[0, -0.44, 0]} castShadow>
              <sphereGeometry args={[0.07, 12, 12]} />
              <meshStandardMaterial color={fur} />
            </mesh>
          </group>

          <group ref={tail} position={[0, 0.02, -0.22]}>
            <mesh position={[0, 0.08, -0.16]} rotation={[0.8, 0, 0]} castShadow>
              <capsuleGeometry args={[0.045, 0.28, 6, 10]} />
              <meshStandardMaterial color={furDark} />
            </mesh>
            <mesh position={[0, 0.18, -0.34]} rotation={[1.1, 0, 0]} castShadow>
              <capsuleGeometry args={[0.035, 0.18, 6, 10]} />
              <meshStandardMaterial color={fur} />
            </mesh>
          </group>

          <group ref={head} position={[0, 0.68, 0.04]}>
            <mesh castShadow>
              <sphereGeometry args={[0.32, 28, 28]} />
              <meshStandardMaterial color={fur} roughness={0.35} />
            </mesh>
            <mesh position={[0, 0.02, 0.18]}>
              <sphereGeometry args={[0.28, 24, 24]} />
              <meshStandardMaterial color="#fff1d4" roughness={0.28} />
            </mesh>
            <mesh position={[-0.16, 0.1, 0.22]} rotation={[0, 0, 0.4]}>
              <sphereGeometry args={[0.1, 16, 16]} />
              <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={0.15} transparent opacity={0.45} />
            </mesh>
            <mesh position={[0.16, 0.1, 0.22]} rotation={[0, 0, -0.4]}>
              <sphereGeometry args={[0.1, 16, 16]} />
              <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={0.15} transparent opacity={0.45} />
            </mesh>

            <mesh position={[-0.09, 0.07, 0.26]}>
              <sphereGeometry args={[0.075, 16, 16]} />
              <meshStandardMaterial color="#fffaf0" />
            </mesh>
            <mesh position={[0.09, 0.07, 0.26]}>
              <sphereGeometry args={[0.075, 16, 16]} />
              <meshStandardMaterial color="#fffaf0" />
            </mesh>
            <mesh ref={pupilL} position={[-0.09, 0.06, 0.325]}>
              <sphereGeometry args={[0.035, 12, 12]} />
              <meshStandardMaterial color="#1b1224" />
            </mesh>
            <mesh ref={pupilR} position={[0.09, 0.06, 0.325]}>
              <sphereGeometry args={[0.035, 12, 12]} />
              <meshStandardMaterial color="#1b1224" />
            </mesh>
            <mesh ref={lidL} position={[-0.09, 0.1, 0.27]}>
              <boxGeometry args={[0.13, 0.05, 0.04]} />
              <meshStandardMaterial color={fur} />
            </mesh>
            <mesh ref={lidR} position={[0.09, 0.1, 0.27]}>
              <boxGeometry args={[0.13, 0.05, 0.04]} />
              <meshStandardMaterial color={fur} />
            </mesh>

            <mesh position={[0, -0.02, 0.34]} rotation={[0.35, 0, 0]} castShadow>
              <sphereGeometry args={[0.14, 16, 16]} />
              <meshStandardMaterial color="#c9a57a" />
            </mesh>
            <mesh position={[0, -0.01, 0.46]}>
              <sphereGeometry args={[0.045, 12, 12]} />
              <meshStandardMaterial color="#1a1210" roughness={0.3} />
            </mesh>

            <group ref={jaw} position={[0, -0.08, 0.34]}>
              <mesh rotation={[0.15, 0, 0]}>
                <capsuleGeometry args={[0.05, 0.1, 6, 10]} />
                <meshStandardMaterial color="#2a1c18" />
              </mesh>
            </group>

            <group ref={earL} position={[-0.22, 0.26, -0.02]} rotation={[0.2, 0, 0.55]}>
              <mesh castShadow>
                <capsuleGeometry args={[0.07, 0.28, 6, 12]} />
                <meshStandardMaterial color={furDark} />
              </mesh>
              <mesh position={[0, 0, 0.02]} scale={[0.7, 0.8, 0.3]}>
                <capsuleGeometry args={[0.06, 0.22, 6, 10]} />
                <meshStandardMaterial color="#e7b8c8" />
              </mesh>
            </group>
            <group ref={earR} position={[0.22, 0.26, -0.02]} rotation={[0.2, 0, -0.55]}>
              <mesh castShadow>
                <capsuleGeometry args={[0.07, 0.28, 6, 12]} />
                <meshStandardMaterial color={furDark} />
              </mesh>
              <mesh position={[0, 0, 0.02]} scale={[0.7, 0.8, 0.3]}>
                <capsuleGeometry args={[0.06, 0.22, 6, 10]} />
                <meshStandardMaterial color="#e7b8c8" />
              </mesh>
            </group>
          </group>
        </group>
      </group>
    </group>
  );
}

function FrameCamera() {
  const { camera, size } = useThree();
  useLayoutEffect(() => {
    const short = size.height < 560;
    const wide = size.width / Math.max(size.height, 1) > 1.25;
    camera.position.set(0, short ? 0.95 : 1.05, short || wide ? 6.8 : 5.4);
    camera.fov = short ? 46 : 40;
    camera.near = 0.1;
    camera.far = 40;
    camera.lookAt(0, 1.05, 0);
    camera.updateProjectionMatrix();
  }, [camera, size.height, size.width]);
  return null;
}

function StageLights({ emotion }) {
  const color = EMOTION_LIGHT[String(emotion || "neutral").toLowerCase()] || EMOTION_LIGHT.neutral;
  const key = useRef();
  const target = useRef(new THREE.Color(color));
  useFrame(() => {
    target.current.set(color);
    if (key.current) key.current.color.lerp(target.current, 0.06);
  });
  return (
    <>
      <hemisphereLight args={["#fff4e5", "#3a2a55", 1.15]} />
      <ambientLight intensity={1.05} color="#f3efe6" />
      <directionalLight
        ref={key}
        position={[0.8, 3.4, 4.4]}
        intensity={2.6}
        color={color}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />
      <directionalLight position={[-2.4, 2.2, 2.2]} intensity={0.9} color="#ffffff" />
      <pointLight position={[0.2, 1.8, 2.4]} intensity={1.35} color="#fff7ea" />
      <pointLight position={[-1.6, 1.4, -1.0]} intensity={0.35} color="#7c3aed" />
    </>
  );
}

export default function GoodBoiStage({
  emotion = "neutral",
  animation = "idle",
  speaking = false,
  thinking = false,
  audioLevel = 0,
}) {
  return (
    <Canvas
      shadows
      dpr={[1, 1.75]}
      camera={{ position: [0, 1.05, 5.4], fov: 40, near: 0.1, far: 40 }}
      gl={{ antialias: true, alpha: true, toneMappingExposure: 1.45 }}
      style={{ width: "100%", height: "100%", display: "block" }}
    >
      <color attach="background" args={["#12121c"]} />
      <fog attach="fog" args={["#12121c", 12, 24]} />
      <FrameCamera />
      <StageLights emotion={emotion} />
      <group scale={0.68} position={[0, 0, 0]}>
        <GoodBoiRig
          emotion={emotion}
          animation={animation}
          speaking={speaking}
          thinking={thinking}
          audioLevel={audioLevel}
        />
      </group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <circleGeometry args={[2.4, 48]} />
        <meshStandardMaterial color="#2a2a38" roughness={0.7} metalness={0.05} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]}>
        <ringGeometry args={[0.85, 0.92, 48]} />
        <meshStandardMaterial color="#7c3aed" emissive="#7c3aed" emissiveIntensity={0.6} />
      </mesh>
    </Canvas>
  );
}
